import React, { useEffect, useId, useState } from 'react';

export type AppSwitchSize = 'default' | 'compact' | 'large';

interface Props {
  checked: boolean;
  onChange: (value: boolean) => void;
  size?: AppSwitchSize;
  disabled?: boolean;
  ariaLabel?: string;
}

const AppSwitch = ({ checked, onChange, size = 'default', disabled = false, ariaLabel }: Props) => {
  const [isChecked, setIsChecked] = useState(checked);
  const switchId = useId();
  const isCompact = size === 'compact';
  const isLarge = size === 'large';

    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);

    const handleChange = () => {
        const newValue = !isChecked;
        setIsChecked(newValue);
        onChange(newValue);
    };

  const containerClassName = isCompact ? 'w-10' : isLarge ? 'w-20' : 'w-12';
  const trackClassName = isCompact ? 'h-5 w-10' : isLarge ? 'h-10 w-20' : 'h-6 w-12';
  const thumbClassName = isCompact
    ? 'top-0.5 left-0.5 h-4 w-4'
    : isLarge
      ? 'top-1 left-1 h-8 w-8'
      : 'top-1 left-0.5 h-4 w-4';
  const checkedThumbClassName = isCompact
    ? 'translate-x-5'
    : isLarge
      ? 'translate-x-10'
      : 'translate-x-6';

  return (
    <div className={`relative inline-block ${containerClassName} align-middle select-none transition duration-200 ease-in ${disabled ? 'opacity-50' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className="hidden"
        id={`switch-${switchId}`}
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
      />
      <label
        data-testid="label-switch"
        htmlFor={`switch-${switchId}`}
        className={disabled ? 'cursor-not-allowed block' : 'cursor-pointer block'}
      >
        <span
          className={`block ${trackClassName} rounded-full transition-colors duration-200 ease-in-out ${
            checked ? 'bg-[#568C13]' : 'bg-gray-300'
          }`}
        />
        <span
          className={`absolute ${thumbClassName} bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
            checked ? checkedThumbClassName : 'translate-x-0'
          }`}
        />
      </label>
    </div>
  );
};

export default AppSwitch;
